import React, { useEffect, useState, useCallback } from 'react';
import { 
  AnalyticalTable, 
  Button, 
  Dialog, 
  Title, 
  Select, 
  Option, 
  BusyIndicator, 
  Input, 
  Bar,
  Form,
  FormItem,
  FormGroup,
  MessageBox,
  MessageBoxType,
  MessageBoxAction,
  Text,
  Icon,
  Label,
  DatePicker
} from '@ui5/webcomponents-react';
import axiosInstance from '../utils/axiosInstance';
import usePermission from '../hooks/usePermission';
import withPermission from './withPermission';
import { rightAlignedNumberStyle, formatNumberWithComma, parseToNumber } from '../utils/validator';
import useAutoTranslate from '../hooks/useAutoTranslate';

const TruckList = () => {
  const { hasPermission } = usePermission();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    sortBy: 'licensePlate',
    sortDirection: 'asc'
  });
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [formData, setFormData] = useState({
    licensePlate: '',
    brand: '',
    model: '',
    year: new Date().getFullYear(),
    capacityTons: 0,
    capacityCubicMeters: 0,
    isActive: true,
    insuranceExpiryDate: '',
    technicalReviewExpiryDate: '',
    truckTypeId: '',
    carrierId: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [truckTypes, setTruckTypes] = useState([]);
  const { autoT } = useAutoTranslate();

  const fetchTrucks = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const url = `trucks?page=${page}&size=${pageSize}`;
      const response = await axiosInstance.get(url);
      if (response.data.records.code === "SUCCESS") {
        setData(response.data.records.data);
        setTotalPages(Math.ceil(response.data.totalCount / pageSize));
      } else {
        setError(response.data.records.message);
      }
    } catch (err) {
      setError('Error al cargar los equipos: ' + (err.message || 'Error desconocido'));
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  useEffect(() => {
    fetchTrucks(currentPage);
  }, [currentPage, fetchTrucks]);

  useEffect(() => {
    let filtered = [...data];
    
    // Filtrar por estado
    if (selectedStatus !== 'all') {
      const isActive = selectedStatus === 'active';
      filtered = filtered.filter(truck => truck.isActive === isActive);
    }
    
    // Filtrar por término de búsqueda
    if (searchTerm) {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(truck => 
        truck.licensePlate.toLowerCase().includes(lowerCaseSearchTerm) ||
        truck.brand.toLowerCase().includes(lowerCaseSearchTerm) ||
        truck.model.toLowerCase().includes(lowerCaseSearchTerm)
      );
    }
    
    // Ordenar los datos
    filtered.sort((a, b) => {
      const valueA = a[sortConfig.sortBy];
      const valueB = b[sortConfig.sortBy];
      
      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return sortConfig.sortDirection === 'asc' 
          ? valueA.localeCompare(valueB) 
          : valueB.localeCompare(valueA);
      } else {
        return sortConfig.sortDirection === 'asc' 
          ? valueA - valueB 
          : valueB - valueA;
      }
    });
    
    setFilteredData(filtered);
  }, [data, selectedStatus, searchTerm, sortConfig]);

  const handleSort = (column) => {
    setSortConfig(prevConfig => ({
      sortBy: column,
      sortDirection: prevConfig.sortBy === column && prevConfig.sortDirection === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleStatusFilterChange = (event) => {
    setSelectedStatus(event.target.value);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  // Función para manejar el clic en una fila
  const handleRowClick = (event) => {
    // Solo permitir editar si tiene el permiso de actualización
    if (!hasPermission('trucks', 'update')) {
      return;
    }

    const { detail } = event;
    if (detail && detail.row && detail.row.original) {
      const truck = detail.row.original;
      setSelectedTruck(truck);
      
      // Inicializar el formulario con los datos del equipo seleccionado
      setFormData({
        licensePlate: truck.licensePlate || '',
        brand: truck.brand || '',
        model: truck.model || '',
        year: truck.year || new Date().getFullYear(),
        capacityTons: truck.capacityTons || 0,
        capacityCubicMeters: truck.capacityCubicMeters || 0,
        isActive: truck.isActive,
        insuranceExpiryDate: truck.insuranceExpiryDate || '',
        technicalReviewExpiryDate: truck.technicalReviewExpiryDate || '',
        truckTypeId: truck.truckTypeId || '',
        carrierId: truck.carrierId || ''
      });
      
      // Mostrar el diálogo de edición
      setShowEditDialog(true);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleCreateClick = () => {
    // Solo permitir crear equipos si tiene el permiso de creación
    if (!hasPermission('trucks', 'create')) {
      return;
    }

    setFormData({
      licensePlate: '',
      brand: '',
      model: '',
      year: new Date().getFullYear(),
      capacityTons: 0,
      capacityCubicMeters: 0,
      isActive: true,
      insuranceExpiryDate: '',
      technicalReviewExpiryDate: '',
      truckTypeId: '',
      carrierId: ''
    });
    setFormErrors({});
    setShowCreateDialog(true);
  };

  const handleEditClick = (truck) => {
    // Solo permitir editar equipos si tiene el permiso de actualización
    if (!hasPermission('trucks', 'update')) {
      return;
    }

    setSelectedTruck(truck);
    setFormData({
      licensePlate: truck.licensePlate || '',
      brand: truck.brand || '',
      model: truck.model || '',
      year: truck.year || new Date().getFullYear(),
      capacityTons: truck.capacityTons || 0,
      capacityCubicMeters: truck.capacityCubicMeters || 0,
      isActive: truck.isActive,
      insuranceExpiryDate: truck.insuranceExpiryDate || '',
      technicalReviewExpiryDate: truck.technicalReviewExpiryDate || '',
      truckTypeId: truck.truckTypeId || '',
      carrierId: truck.carrierId || ''
    });
    setFormErrors({});
    setShowEditDialog(true);
  };

  const handleDeleteClick = (truck) => {
    // Solo permitir eliminar equipos si tiene el permiso de eliminación
    if (!hasPermission('trucks', 'delete')) {
      return;
    }

    setSelectedTruck(truck);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedTruck) return;
    
    try {
      await axiosInstance.delete(`trucks/${selectedTruck.id}`);
      fetchTrucks(currentPage);
      setShowDeleteConfirm(false);
    } catch (err) {
      setError('Error al eliminar el equipo: ' + (err.message || 'Error desconocido'));
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpiar el error del campo cuando el usuario comienza a escribir
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleNumberInputChange = (event) => {
    const { name, value } = event.target;
      setFormData({
        ...formData,
        [name]: value
      });
    if (formErrors[name]) {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleDateChange = (event) => {
    const { name, value } = event.detail;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleToggleActive = () => {
    setFormData(prev => ({
      ...prev,
      isActive: !prev.isActive
    }));
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.licensePlate) {
      errors.licensePlate = 'La placa es obligatoria';
    }
    
    if (!formData.brand) {
      errors.brand = 'La marca es obligatoria';
    }
    
    if (!formData.model) {
      errors.model = 'El modelo es obligatorio';
    }
    
    if (!formData.year || formData.year < 1900 || formData.year > new Date().getFullYear() + 1) {
      errors.year = 'El año debe ser válido';
    }
    
    if (!formData.truckTypeId) {
      errors.truckTypeId = 'El tipo de equipo es obligatorio';
    }
    
    if (!formData.carrierId) {
      errors.carrierId = 'El transportista es obligatorio';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateSubmit = async () => {
    if (!validateForm()) return;
    
    try {
      const response = await axiosInstance.post('trucks', formData);
      if (response.status === 201 || response.status === 200) {
        fetchTrucks(currentPage);
        setShowCreateDialog(false);
      } else {
        setError('Error al crear el equipo: ' + (response.data.message || 'Error desconocido'));
      }
    } catch (err) {
      setError('Error al crear el equipo: ' + (err.message || 'Error desconocido'));
    }
  };

  const handleEditSubmit = async () => {
    if (!validateForm() || !selectedTruck) return;
    
    try {
      const response = await axiosInstance.put(`trucks/${selectedTruck.id}`, formData);
      if (response.status === 200) {
        fetchTrucks(currentPage);
        setShowEditDialog(false);
      } else {
        setError('Error al actualizar el equipo: ' + (response.data.message || 'Error desconocido'));
      }
    } catch (err) {
      setError('Error al actualizar el equipo: ' + (err.message || 'Error desconocido'));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES');
  };

  const columns = [
    {
      Header: 'Placa patente',
      accessor: 'licensePlate',
      headerProps: {
        onClick: () => handleSort('licensePlate')
      },
      className: sortConfig.sortBy === 'licensePlate' ? `sorted-${sortConfig.sortDirection}` : ''
    },
    {
      Header: 'Marca',
      accessor: 'brand',
      headerProps: {
        onClick: () => handleSort('brand')
      },
      className: sortConfig.sortBy === 'brand' ? `sorted-${sortConfig.sortDirection}` : ''
    },
    {
      Header: 'Modelo',
      accessor: 'model',
      headerProps: {
        onClick: () => handleSort('model')
      },
      className: sortConfig.sortBy === 'model' ? `sorted-${sortConfig.sortDirection}` : ''
    },
    {
      Header: 'Año',
      accessor: 'year',
      headerProps: {
        onClick: () => handleSort('year')
      },
      className: sortConfig.sortBy === 'year' ? `sorted-${sortConfig.sortDirection}` : ''
    },
    {
      Header: 'Capacidad (kg)',
      accessor: 'capacityTons',
      headerProps: {
        onClick: () => handleSort('capacityTons')
      },
      className: sortConfig.sortBy === 'capacityTons' ? `sorted-${sortConfig.sortDirection}` : '',
      Cell: ({ value }) => (
        <Text style={rightAlignedNumberStyle()}>{formatNumberWithComma(value)}</Text>
      )
    },
    {
      Header: 'Capacidad (m³)',
      accessor: 'capacityCubicMeters',
      headerProps: {
        onClick: () => handleSort('capacityCubicMeters')
      },
      className: sortConfig.sortBy === 'capacityCubicMeters' ? `sorted-${sortConfig.sortDirection}` : '',
      Cell: ({ value }) => (
        <Text style={rightAlignedNumberStyle()}>{formatNumberWithComma(value)}</Text>
      )
    },
    {
      Header: 'Estado',
      accessor: 'isActive',
      Cell: ({ value }) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Icon 
            name={value ? "status-positive" : "status-negative"} 
            style={{ marginRight: '8px', color: value ? '#28a745' : '#dc3545' }} 
          />
          <Text>{value ? 'Activo' : 'Inactivo'}</Text>
        </div>
      ),
      headerProps: {
        onClick: () => handleSort('isActive')
      },
      className: sortConfig.sortBy === 'isActive' ? `sorted-${sortConfig.sortDirection}` : ''
    },
    {
      Header: 'Venc. Seguro',
      accessor: 'insuranceExpiryDate',
      Cell: ({ value }) => formatDate(value),
      headerProps: {
        onClick: () => handleSort('insuranceExpiryDate')
      },
      className: sortConfig.sortBy === 'insuranceExpiryDate' ? `sorted-${sortConfig.sortDirection}` : ''
    },
    {
      Header: 'Venc. Rev. Técnica',
      accessor: 'technicalReviewExpiryDate',
      Cell: ({ value }) => formatDate(value),
      headerProps: {
        onClick: () => handleSort('technicalReviewExpiryDate')
      },
      className: sortConfig.sortBy === 'technicalReviewExpiryDate' ? `sorted-${sortConfig.sortDirection}` : ''
    },
    {
      Header: 'Acciones',
      id: 'actions',
      Cell: ({ row }) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          {hasPermission('trucks', 'update') && (
            <Button 
              icon="edit" 
              design="Transparent" 
              onClick={(e) => {
                e.stopPropagation();
                handleEditClick(row.original);
              }}
            />
          )}
          {hasPermission('trucks', 'delete') && (
            <Button 
              icon="delete" 
              design="Transparent" 
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(row.original);
              }}
            />
          )}
        </div>
      )
    }
  ];

  // Función para cargar los tipos de equipo
  const loadTruckTypes = async () => {
    try {
      const response = await axiosInstance.get('/truck-types');
      console.log('Respuesta de tipos de equipo:', response);
      
      // Verificar si la respuesta tiene la estructura esperada
      if (response.data && response.data.records && response.data.records.data) {
        console.log('Tipos de equipo cargados:', response.data.records.data);
        setTruckTypes(response.data.records.data);
      } else {
        console.error('Estructura de respuesta inesperada:', response.data);
      }
    } catch (error) {
      console.error('Error al cargar tipos de equipo:', error);
    }
  };

  // Asegurarnos de que se carguen los tipos de equipo cuando se abren los diálogos
  useEffect(() => {
    if (showCreateDialog || showEditDialog) {
      loadTruckTypes();
    }
  }, [showCreateDialog, showEditDialog]);

  return (
    <div style={{
      padding: '1.5rem',
      backgroundColor: 'var(--sapBackgroundColor)',
      minHeight: '100vh'
    }}>
      <div style={{ padding: '1rem' }}>
        <Title level="H2">{autoT('Gestión de equipos')}</Title>
        
        {error && (
          <div style={{ color: 'red', margin: '1rem 0' }}>
            {error}
          </div>
        )}
        
        <Bar
          startContent={
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <Input
                placeholder={autoT('Buscar equipo...')}
                value={searchTerm}
                onChange={handleSearchChange}
                style={{ width: '250px' }}
              />
              <Select
                onChange={handleStatusFilterChange}
                value={selectedStatus}
              >
                <Option value="all">{autoT('Todos')}</Option>
                <Option value="active">{autoT('Activos')}</Option>
                <Option value="inactive">{autoT('Inactivos')}</Option>
              </Select>
            </div>
          }
          endContent={
            hasPermission('trucks', 'create') && (
              <Button 
                icon="add" 
                design="Emphasized"
                onClick={handleCreateClick}
              >
                {autoT('Nuevo Equipo')}
              </Button>
            )
          }
        />
        
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
            <BusyIndicator active size="Large" />
          </div>
        ) : (
          <>
            <AnalyticalTable
              data={filteredData}
              columns={columns}
              selectionMode="None"
              onRowClick={hasPermission('trucks', 'update') ? handleRowClick : undefined}
              style={{ marginTop: '1rem' }}
              visibleRows={10}
              minRows={10}
              alternateRowColor
            />
            
            <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '1rem' }}>
              <Button 
                icon="navigation-left-arrow" 
                disabled={currentPage === 1}
                onClick={handlePrevPage}
              >
                Anterior
              </Button>
              <Text style={{ alignSelf: 'center' }}>
                Página {currentPage} de {totalPages}
              </Text>
              <Button 
                icon="navigation-right-arrow" 
                disabled={currentPage === totalPages}
                onClick={handleNextPage}
                iconEnd
              >
                Siguiente
              </Button>
            </div>
          </>
        )}
      </div>
      
      {/* Dialog para crear equipo */}
      <Dialog
        open={showCreateDialog}
        header={
          <Bar
            startContent={<Title level="H4">{showCreateDialog ? autoT('Crear Nuevo Equipo') : `${autoT('Editar Equipo')}: ${selectedTruck?.licensePlate}`}</Title>}
          />
        }
        onClose={() => setShowCreateDialog(false)}
        style={{ width: '800px' }}
        footer={
          <Bar
            endContent={
              <>
                <Button design="Emphasized" onClick={handleCreateSubmit}>{autoT('Guardar')}</Button>
                <Button design="Transparent" onClick={() => setShowCreateDialog(false)}>{autoT('Cancelar')}</Button>
              </>
            }
          />
        }
      >
        <Form 
          style={{ padding: '1rem' }}
          titleText={autoT('Información del Equipo')}
          backgroundDesign="Transparent"
          columnsXL={2}
          columnsL={2}
          columnsM={2}
          columnsS={1}
          labelSpanXL={4}
          labelSpanL={4}
          labelSpanM={12}
          labelSpanS={12}
        >
          {formErrors.general && (
            <FormItem>
              <Text style={{ color: 'var(--sapErrorColor)', marginBottom: '1rem' }}>{formErrors.general}</Text>
            </FormItem>
          )}
          
          <FormGroup titleText={autoT('Información Básica')}>
            <FormItem labelContent={<Label>{autoT('Placa patente')}</Label>}>
              <Input 
                name="licensePlate" 
                value={formData.licensePlate} 
                onChange={handleInputChange}
                valueState={formErrors.licensePlate ? "Error" : "None"}
                valueStateMessage={formErrors.licensePlate}
                required
              />
            </FormItem>
            <FormItem labelContent={<Label>{autoT('Marca')}</Label>}>
              <Input 
                name="brand" 
                value={formData.brand} 
                onChange={handleInputChange}
                valueState={formErrors.brand ? "Error" : "None"}
                valueStateMessage={formErrors.brand}
                required
              />
            </FormItem>
            <FormItem labelContent={<Label>{autoT('Modelo')}</Label>}>
              <Input 
                name="model" 
                value={formData.model} 
                onChange={handleInputChange}
                valueState={formErrors.model ? "Error" : "None"}
                valueStateMessage={formErrors.model}
                required
              />
            </FormItem>
            <FormItem labelContent={<Label>{autoT('Año')}</Label>}>
              <Input 
                name="year" 
                type="Number"
                value={formData.year} 
                onChange={handleNumberInputChange}
                valueState={formErrors.year ? "Error" : "None"}
                valueStateMessage={formErrors.year}
                required
              />
            </FormItem>
          </FormGroup>
          
          <FormGroup titleText={autoT('Capacidad')}>
            <FormItem labelContent={<Label>{autoT('Capacidad en Kilogramos')}</Label>}>
              <Input 
                name="capacityTons" 
                type="text"
                value={formatNumberWithComma(formData.capacityTons)}
                onChange={handleNumberInputChange}
              />
            </FormItem>
            <FormItem labelContent={<Label>{autoT('Capacidad en Metros Cúbicos')}</Label>}>
              <Input 
                name="capacityCubicMeters" 
                type="text"
                value={formatNumberWithComma(formData.capacityCubicMeters)} 
                onChange={handleNumberInputChange}
              />
            </FormItem>
          </FormGroup>
          
          <FormGroup titleText={autoT('Fechas de vencimiento')}>
            <FormItem labelContent={<Label>{autoT('Vencimiento de seguro')}</Label>}>
              <DatePicker 
                name="insuranceExpiryDate"
                value={formData.insuranceExpiryDate}
                onChange={handleDateChange}
                formatPattern="yyyy-MM-dd"
              />
            </FormItem>
            <FormItem labelContent={<Label>{autoT('Vencimiento de revisión técnica')}</Label>}>
              <DatePicker 
                name="technicalReviewExpiryDate"
                value={formData.technicalReviewExpiryDate}
                onChange={handleDateChange}
                formatPattern="yyyy-MM-dd"
              />
            </FormItem>
          </FormGroup>
          
          <FormGroup titleText={autoT('Información Adicional')}>
            <FormItem labelContent={<Label>{autoT('Tipo de equipo')}</Label>}>
              <Select
                name="truckTypeId"
                value={formData.truckTypeId}
                onChange={(e) => handleInputChange({
                  target: {
                    name: 'truckTypeId',
                    value: e.detail.selectedOption.dataset.key
                  }
                })}
                valueState={formErrors.truckTypeId ? "Error" : "None"}
                valueStateMessage={formErrors.truckTypeId}
                required
              >
                <Option value="" data-key="" selected={formData.truckTypeId === ''}>{autoT('Seleccionar tipo de equipo')}</Option>
                {truckTypes && truckTypes.length > 0 ? (
                  truckTypes.map(type => (
                    <Option key={type.id} value={type.id} data-key={type.id} selected={formData.truckTypeId === type.id}>
                      {type.name || type.description}
                    </Option>
                  ))
                ) : (
                  <Option value="">{autoT('No hay tipos de equipo disponibles')}</Option>
                )}
              </Select>
            </FormItem>
            <FormItem labelContent={<Label>{autoT('Estado')}</Label>}>
              <Button 
                icon={formData.isActive ? "status-positive" : "status-negative"}
                design="Transparent"
                onClick={handleToggleActive}
              >
                {formData.isActive ? autoT('Activo') : autoT('Inactivo')}
              </Button>
            </FormItem>
          </FormGroup>
        </Form>
      </Dialog>

      {/* Dialog para editar equipo */}
      <Dialog
        open={showEditDialog}
        header={
          <Bar
            startContent={<Title level="H4">{autoT('Editar Equipo')}: {selectedTruck?.licensePlate}</Title>}
          />
        }
        onClose={() => setShowEditDialog(false)}
        style={{ width: '800px' }}
        footer={
          <Bar
            startContent={
              hasPermission('trucks', 'delete') && (
                <Button design="Negative" onClick={handleDeleteClick} icon="delete">{autoT('Eliminar')}</Button>
              )
            }
            endContent={
              <>
                <Button design="Emphasized" onClick={handleEditSubmit}>{autoT('Guardar')}</Button>
                <Button design="Transparent" onClick={() => setShowEditDialog(false)}>{autoT('Cancelar')}</Button>
              </>
            }
          />
        }
      >
        <Form 
          style={{ padding: '1rem' }}
          titleText={autoT('Información del Equipo')}
          backgroundDesign="Transparent"
          columnsXL={2}
          columnsL={2}
          columnsM={2}
          columnsS={1}
          labelSpanXL={4}
          labelSpanL={4}
          labelSpanM={12}
          labelSpanS={12}
        >
          {formErrors.general && (
            <FormItem>
              <Text style={{ color: 'var(--sapErrorColor)', marginBottom: '1rem' }}>{formErrors.general}</Text>
            </FormItem>
          )}
          
          <FormGroup titleText={autoT('Información Básica')}>
            <FormItem labelContent={<Label>{autoT('Placa patente')}</Label>}>
              <Input 
                name="licensePlate" 
                value={formData.licensePlate} 
                onChange={handleInputChange}
                valueState={formErrors.licensePlate ? "Error" : "None"}
                valueStateMessage={formErrors.licensePlate}
                required
              />
            </FormItem>
            <FormItem labelContent={<Label>{autoT('Marca')}</Label>}>
              <Input 
                name="brand" 
                value={formData.brand} 
                onChange={handleInputChange}
                valueState={formErrors.brand ? "Error" : "None"}
                valueStateMessage={formErrors.brand}
                required
              />
            </FormItem>
            <FormItem labelContent={<Label>{autoT('Modelo')}</Label>}>
              <Input 
                name="model" 
                value={formData.model} 
                onChange={handleInputChange}
                valueState={formErrors.model ? "Error" : "None"}
                valueStateMessage={formErrors.model}
                required
              />
            </FormItem>
            <FormItem labelContent={<Label>{autoT('Año')}</Label>}>
              <Input 
                name="year" 
                type="Number"
                value={formData.year} 
                onChange={handleNumberInputChange}
                valueState={formErrors.year ? "Error" : "None"}
                valueStateMessage={formErrors.year}
                required
              />
            </FormItem>
          </FormGroup>
          
          <FormGroup titleText={autoT('Capacidad')}>
            <FormItem labelContent={<Label>{autoT('Capacidad en kilogramos')}</Label>}>
              <Input 
                name="capacityTons" 
                type="text"
                value={formatNumberWithComma(formData.capacityTons)}
                onChange={handleNumberInputChange}
              />
            </FormItem>
            <FormItem labelContent={<Label>{autoT('Capacidad en Metros Cúbicos')}</Label>}>
              <Input 
                name="capacityCubicMeters" 
                type="text"
                value={formatNumberWithComma(formData.capacityCubicMeters)} 
                onChange={handleNumberInputChange}
              />
            </FormItem>
          </FormGroup>
          
          <FormGroup titleText={autoT('Fechas de vencimiento')}>
            <FormItem labelContent={<Label>{autoT('Vencimiento de seguro')}</Label>}>
              <DatePicker 
                name="insuranceExpiryDate"
                value={formData.insuranceExpiryDate}
                onChange={handleDateChange}
                formatPattern="yyyy-MM-dd"
              />
            </FormItem>
            <FormItem labelContent={<Label>{autoT('Vencimiento de revisión técnica')}</Label>}>
              <DatePicker 
                name="technicalReviewExpiryDate"
                value={formData.technicalReviewExpiryDate}
                onChange={handleDateChange}
                formatPattern="yyyy-MM-dd"
              />
            </FormItem>
          </FormGroup>
          
          <FormGroup titleText={autoT('Información Adicional')}>
            <FormItem labelContent={<Label>{autoT('Tipo de equipo')}</Label>}>
              <Select
                name="truckTypeId"
                value={formData.truckTypeId}
                onChange={(e) => handleInputChange({
                  target: {
                    name: 'truckTypeId',
                    value: e.detail.selectedOption.dataset.key
                  }
                })}
                valueState={formErrors.truckTypeId ? "Error" : "None"}
                valueStateMessage={formErrors.truckTypeId}
                required
              >
                <Option value="" data-key="" selected={formData.truckTypeId === ''}>{autoT('Seleccionar tipo de equipo')}</Option>
                {truckTypes && truckTypes.length > 0 ? (
                  truckTypes.map(type => (
                    <Option key={type.id} value={type.id} data-key={type.id} selected={formData.truckTypeId === type.id}>
                      {type.name || type.description}
                    </Option>
                  ))
                ) : (
                  <Option value="">{autoT('No hay tipos de equipo disponibles')}</Option>
                )}
              </Select>
            </FormItem>
            <FormItem labelContent={<Label>{autoT('Estado')}</Label>}>
              <Button 
                icon={formData.isActive ? "status-positive" : "status-negative"}
                design="Transparent"
                onClick={handleToggleActive}
              >
                {formData.isActive ? autoT('Activo') : autoT('Inactivo')}
              </Button>
            </FormItem>
          </FormGroup>
        </Form>
      </Dialog>

      {/* Confirmación de eliminación */}
      <MessageBox
        open={showDeleteConfirm}
        onClose={(event) => {
          if (event.detail.action === MessageBoxAction.OK) {
            handleConfirmDelete();
          } else {
            setShowDeleteConfirm(false);
          }
        }}
        title={autoT('Confirmar eliminación')}
        type={MessageBoxType.WARNING}
        actions={[MessageBoxAction.OK, MessageBoxAction.CANCEL]}
      >
        {autoT('¿Está seguro que desea eliminar el equipo')} <strong>{selectedTruck?.licensePlate}</strong>? {autoT('Esta acción no se puede deshacer.')}
      </MessageBox>
    </div>
  );
};

export default withPermission(TruckList, 'trucks', 'listView');
